import React, { useState, useEffect } from "react";
import { Row, Col, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import ownerPropertyServices from "../../services/owner-services/owner-property-services";
import { handleErrors } from "../../utils/errorHandler";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { ChevronDownIcon } from "primereact/icons/chevrondown";
import { ChevronRightIcon } from "primereact/icons/chevronright";
import { Column } from "primereact/column";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import dummyImage from "../../assets/images/dummy-image-landscape.jpg";
import * as XLSX from "xlsx";
import { Calendar } from "primereact/calendar";

const OwnerProperty = () => {
  const { propertyGuid } = useParams();
  const [propertyList, setPropertyList] = useState([]);
  // Stores flat name + guid list (with "All" as first entry)
  const [flatnameWithGuid, setFlatnameWithGuid] = useState([]);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showFlatmateModal, setShowFlatmateModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [tenantData, setTenantData] = useState([]);
  const [flatmateData, setFlatmateData] = useState([]);
  const [propertyName, setPropertyName] = useState("");
  const [loading, setLoading] = useState(false);

  const [totalRent, setTotalRent] = useState(0);
  const [totalcount, setToatalCount] = useState(0);
  const [isLastPage, setisLastPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [first, setFirst] = useState(0);
  const pageData = 10;

  // Flat Occupancy Report States
  const [flatOccupancyData, setFlatOccupancyData] = useState([]);
  const [loadingFlatOccupancyReport, setLoadingFlatOccupancyReport] =
    useState(false);
  const [reportTotalCount, setReportTotalCount] = useState(0);
  const [reportCurrentPage, setReportCurrentPage] = useState(0);
  const [reportFirst, setReportFirst] = useState(0);
  const reportsPerPage = 10;
  const [showFlatOccupancyReport, setShowFlatOccupancyReport] = useState({
    flatGuid: "",
    show: false,
    flatName: "",
  });

  // Month picker states (stores a Date object representing the selected month)
  const [flatFromMonth, setFlatFromMonth] = useState(null);
  const [flatToMonth, setFlatToMonth] = useState(null);

  const [exportingExcel, setExportingExcel] = useState(false);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const formatINRRent = (amount) => {
    if (amount >= 10000) {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
    return `₹${amount}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /**
   * Given a Date object representing any day in a month,
   * returns { startDate, endDate } as "YYYY-MM-DD" strings for
   * the first and last day of that month.
   */
  const getMonthDateRange = (date) => {
    if (!date) return { startDate: null, endDate: null };
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0); // day 0 of next month = last day of this month
    return {
      startDate: formatDateForAPI(firstDay),
      endDate: formatDateForAPI(lastDay),
    };
  };

  // ─── Pagination ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (totalcount > 0) {
      const lastPageIndex = Math.floor((totalcount - 1) / pageData);
      setisLastPage(currentPage === lastPageIndex);
      window.scrollTo(0, 0);
    }
  }, [currentPage, totalcount]);

  // ─── Fetch property list ─────────────────────────────────────────────────────

  useEffect(() => {
    async function fetchPropertyData() {
      try {
        setLoading(true);
        const response = await ownerPropertyServices.getProperty(propertyGuid);
        setPropertyName(response?.data?.result[0].propertyName);

        const formattedProperties = response?.data?.result?.map((property) => ({
          ...property,
          ownerRent: formatINRRent(property.ownerRent),
        }));

        
        setFlatnameWithGuid([
          { flatGuid: "", flatName: "All" },
          ...(response?.data?.result?.map((property) => ({
            flatGuid: property.flatGuid,
            flatName: property.flatName,
          })) || []),
        ]);

        setPropertyList(formattedProperties || []);
        setToatalCount(formattedProperties.length);

        const totalOwnerRent = response?.data?.result?.reduce(
          (sum, property) => sum + property.ownerRent,
          0
        );
        setTotalRent(totalOwnerRent);
      } catch (error) {
        toast.error(handleErrors(error));
      } finally {
        setLoading(false);
      }
    }

    if (propertyGuid) {
      fetchPropertyData();
    }
  }, [propertyGuid]);

  // ─── Fetch Flat Occupancy Report ──────────────────────────────────────────────

  const fetchFlatOccupancyReport = async (
    page = 0,
    pageSize = reportsPerPage,
    fromMonth = flatFromMonth,
    toMonth = flatToMonth
  ) => {
    setLoadingFlatOccupancyReport(true);
    try {
      const { startDate } = getMonthDateRange(fromMonth);
      const { endDate } = getMonthDateRange(fromMonth); //i have change to enddate for same month  if they want multiple month just pass toonth here
      
      const payload = {
        propertyGuid: propertyGuid,
        flatGuid: showFlatOccupancyReport.flatGuid, 
        startDate: startDate,
        endDate: endDate,
        pageSize: pageSize,
        pageNumber: page + 1,
      };
      const response =
        await ownerPropertyServices.getFlatOccupancyReport(payload);
      const { data } = response;
      setFlatOccupancyData(data?.result || []);
      setReportTotalCount(data?.result?.[0]?.totalRows || 0);
    } catch (error) {
      toast.error(handleErrors(error));
    } finally {
      setLoadingFlatOccupancyReport(false);
    }
  };

  useEffect(() => {
    if (
      !propertyGuid ||
      !showFlatOccupancyReport.show
    )
      return;
    fetchFlatOccupancyReport(reportCurrentPage);
  
  }, [ showFlatOccupancyReport.show]);

  // ─── Filter actions ───────────────────────────────────────────────────────────

  const handleApplyFilter = () => {
    setReportCurrentPage(0);
    setReportFirst(0);
    fetchFlatOccupancyReport(0, reportsPerPage, flatFromMonth, flatToMonth);
  };

  const handleResetFilter = () => {
    setFlatFromMonth(null);
    setFlatToMonth(null);
    setReportCurrentPage(0);
    setReportFirst(0);

    // Call API directly with null dates 
    setLoadingFlatOccupancyReport(true);
    ownerPropertyServices
      .getFlatOccupancyReport({
        propertyGuid: propertyGuid,
        flatGuid: showFlatOccupancyReport.flatGuid,
        startDate: null,
        endDate: null,
        pageSize: reportsPerPage,
        pageNumber: 1,
      })
      .then((response) => {
        const { data } = response;
        setFlatOccupancyData(data?.result || []);
        setReportTotalCount(data?.result?.[0]?.totalRows || 0);
      })
      .catch((error) => toast.error(handleErrors(error)))
      .finally(() => setLoadingFlatOccupancyReport(false));
  };

  // ─── Export Excel ─────────────────────────────────────────────────────────────

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const { startDate } = getMonthDateRange(flatFromMonth);
      const { endDate } = getMonthDateRange(flatFromMonth);
      //i have change to enddate for same month  if they want multiple month just pass to date here

      const payload = {
        propertyGuid: propertyGuid,
        flatGuid: showFlatOccupancyReport.flatGuid,
        startDate: startDate || null,
        endDate: endDate || null,
        pageSize: reportTotalCount || 1000,
        pageNumber: 1,
      };
      const response =
        await ownerPropertyServices.getFlatOccupancyReport(payload);
      const allRows = response?.data?.result || [];

      const excelData = allRows.map((row, index) => ({
        "S.No": index + 1,
        "Flat Name": row.flatName || "-",
        "Flat No": row.flatNo || "-",
        "Period Type": row.periodType || "-",
        "Tenant Name": row.tenantName || "-",
        "Tenant Mobile": row.tenantMobile || "-",
        "Order Status": row.orderStatus || "-",
        "Effective From": formatDate(row.effectiveFrom),
        "Effective To": formatDate(row.effectiveTo),
        "Days In Range": row.daysInRange ?? "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const colWidths = Object.keys(excelData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Occupancy Report");
      // Page 2 - Summary
const summaryData = [
  {
    "Property Occupancy Percentage":
      allRows?.[0]?.propertyOccupancyPercentage ?? "-",

    "Flat Occupancy Percentage":
      allRows?.[0]?.flatOccupancyPercentage ?? "-",
  },
];

const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);

summaryWorksheet["!cols"] = [
  { wch: 35 },
  { wch: 30 },
];

XLSX.utils.book_append_sheet(
  workbook,
  summaryWorksheet,
  "Occupancy Summary"
);

      const fileName = `${showFlatOccupancyReport.flatName || "Flat"}_Occupancy_Report.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      toast.error(handleErrors(error));
    } finally {
      setExportingExcel(false);
    }
  };

  // ─── Modal helpers ────────────────────────────────────────────────────────────

  const handleCloseFlatOccupancyModal = () => {
    setShowFlatOccupancyReport({ flatGuid: "", show: false, flatName: "" });
    setFlatOccupancyData([]);
    setReportTotalCount(0);
    setReportCurrentPage(0);
    setReportFirst(0);
    setFlatFromMonth(null);
    setFlatToMonth(null);
  };

  const handleViewTenants = (title, data) => {
    setModalTitle(title);
    setTenantData(data);
    setShowTenantModal(true);
  };

  const handleViewFlatmates = (title, data) => {
    setModalTitle(title);
    setFlatmateData(data);
    setShowFlatmateModal(true);
  };

  // ─── Table body renderers ─────────────────────────────────────────────────────

  const propertyNameWithFlatNo = (rowData) => (
    <div
      style={{ display: "flex", alignItems: "center", gap: "8px" }}
      className="flex-wrap"
    >
      <img
        src={rowData.flatImage || dummyImage}
        alt={`Flat ${rowData.flatNo}`}
        style={{
          width: "50px",
          height: "50px",
          objectFit: "cover",
          borderRadius: "4px",
        }}
      />
      <div className="min-width-100">
        <b>Flat No: </b> {rowData.flatNo} <b>({rowData.bhkType})</b>{" "}
        <b>({rowData.flatSize} sq ft)</b>
      </div>
    </div>
  );

  const flatmateName = (rowData) => (
    <div>
      <div>
        {rowData.firstName} {rowData.lastName}
      </div>
      <div>
        <i className="ri-mail-fill"></i>{" "}
        <span style={{ fontWeight: 600 }}>{rowData.emailAddress}</span>
      </div>
      <div>
        <i className="ri-phone-fill"></i>{" "}
        <span style={{ fontWeight: 600 }}>{rowData.contactNo}</span>
      </div>
    </div>
  );

  const viewTenants = (rowData) =>
    rowData.tanents ? (
      <Button
        variant="link"
        onClick={() => handleViewTenants("Tenants", [rowData.tanents])}
      >
        View
      </Button>
    ) : (
      <div>-</div>
    );

  const viewFlatmates = (rowData) =>
    rowData.flatmates?.length ? (
      <Button
        variant="link"
        onClick={() => handleViewFlatmates("Flatmates", rowData.flatmates)}
      >
        View
      </Button>
    ) : (
      <div>-</div>
    );

  const viewFlatOccupancyReport = (rowData) => (
    <Button
      variant="link"
      onClick={() =>
        setShowFlatOccupancyReport({
          flatGuid: rowData.flatGuid,
          show: true,
          flatName: rowData.flatName || rowData.flatNo,
        })
      }
    >
      View Report
    </Button>
  );

  // Badge color for period type
  const periodTypeBadge = (rowData) => (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor:
          rowData.periodType === "Occupied" ? "#d4edda" : "#fff3cd",
        color: rowData.periodType === "Occupied" ? "#155724" : "#856404",
      }}
    >
      {rowData.periodType}
    </span>
  );

  // ─── Dropdown helpers ─────────────────────────────────────────────────────────

  /**
   * The selected flat object (full { flatGuid, flatName }) for the Dropdown value prop.
   * Falls back to the "All" option when flatGuid is "".
   */
  const selectedFlatOption = flatnameWithGuid.find(
    (item) => item.flatGuid === showFlatOccupancyReport.flatGuid
  ) ?? flatnameWithGuid[0]; // default to "All"

  const FlatOptionTemplate = (option) => (
    <div className="flex align-items-center">
      <div>{option.flatName}</div>
    </div>
  );

  const FlatValueTemplate = (option) => {
    if (!option) return <span>Select a flat</span>;
    return <span>{option.flatName}</span>;
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        .OwnerRents .p-datatable-footer { order: -1; }
        .OwnerRents .p-datatable-wrapper { display: flex; flex-direction: column; }
        .p-paginator-bottom.p-paginator.p-component { padding: 0px !important; }
        .filter-bar {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 16px;
          padding: 12px 0;
          border-bottom: 1px solid #dee2e6;
        }
        .filter-bar label {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
          display: block;
        }
.p-calendar input {
        border-radius: 5px 0px 0px 5px;
      }
        .p-dropdown
        {
        border-radius: 5px !important;
            height: 38px;
        }
  .p-dropdown-panel {
    z-index: 9999 !important;
  }
    .p-paginator-current
    {
    position: absolute;
    right:0;
    font-size:13px;
    }
      `}</style>

      <div className="agreement-list">
        <h3>
          {propertyName ? `${propertyName} - Property List` : "Property List"}
        </h3>

        {loading ? (
          <div style={{ textAlign: "center", marginTop: "2rem" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <Row>
            <Col xs={12}>
              <DataTable
                className="OwnerRents"
                value={propertyList}
                paginator
                rows={pageData}
                showGridlines
                stripedRows
                first={first}
                onPage={(e) => {
                  setFirst(e.first);
                  setCurrentPage(e.page);
                }}
                paginatorTemplate={{
                  layout:
                    "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
                  FirstPageLink: (options) => (
                    <>
                      {isLastPage && (
                        <div
                          style={{
                            width: "100%",
                            textAlign: "right",
                            fontWeight: "bold",
                            padding: "20px",
                            borderBottom: "1px solid #dee2e6",
                            backgroundColor: "#f5f5f5",
                            marginBottom: "10px",
                          }}
                        >
                          Total Rent: ₹{totalRent?.toLocaleString("en-IN")}
                        </div>
                      )}
                      {options.element}
                    </>
                  ),
                }}
              >
                <Column header="Property" body={propertyNameWithFlatNo} />
                <Column header="Tenants" body={viewTenants} />
                <Column header="Flatmates" body={viewFlatmates} />
                <Column
                  header="Occupancy Report"
                  body={viewFlatOccupancyReport}
                />
                <Column
                  field="lastMovedOut"
                  header="Last Moved Out"
                  body={(rowData) =>
                    rowData.lastMovedOut ? formatDate(rowData.lastMovedOut) : "-"
                  }
                />
                <Column field="ownerRent" header="Rent" />
              </DataTable>
            </Col>
          </Row>
        )}
      </div>

      {/* ── Tenant Modal ──────────────────────────────────────────────────────── */}
      <Modal
        show={showTenantModal}
        onHide={() => setShowTenantModal(false)}
        size="lg"
      >
        <Modal.Body>
          {tenantData.length ? (
            <ul>
              {tenantData.map((item, index) => (
                <ul key={index} className="m-2">
                  <div className="profile-info-fields">
                    <h4>Tenant Details</h4>
                    <li className="tenant-info">
                      <div className="p-2">
                        <div className="contact-label">
                          <b>Tenant Name</b>
                        </div>
                        <div className="contact-value">{item.userName}</div>
                      </div>
                      <div className="p-2">
                        <div className="contact-label">
                          <b>Contact</b>
                        </div>
                        <div className="contact-value">{item.contactNo}</div>
                      </div>
                    </li>
                  </div>
                  <div className="profile-info-fields">
                    <h4>KYC</h4>
                    <li className="tenant-info">
                      <div className="p-2">
                        <div className="contact-label">
                          <b>
                            Aadhar Number{" "}
                            <FontAwesomeIcon
                              icon={
                                item.aadharNoStatus
                                  ? faCheckCircle
                                  : faTimesCircle
                              }
                              color={item.aadharNoStatus ? "green" : "red"}
                            />
                          </b>
                        </div>
                        <div className="contact-value">{item.aadharNo}</div>
                      </div>
                      <div className="p-2">
                        <div className="contact-label">
                          <b>
                            PAN Number{" "}
                            <FontAwesomeIcon
                              icon={
                                item.panStatus ? faCheckCircle : faTimesCircle
                              }
                              color={item.panStatus ? "green" : "red"}
                            />
                          </b>
                        </div>
                        <div className="contact-value">{item.pan}</div>
                      </div>
                      <div className="p-2">
                        <div className="contact-label">
                          <b>
                            Documents Status{" "}
                            <FontAwesomeIcon
                              icon={
                                item.docVerifyStatus === "Verified"
                                  ? faCheckCircle
                                  : faTimesCircle
                              }
                              color={
                                item.docVerifyStatus === "Verified"
                                  ? "green"
                                  : "red"
                              }
                            />
                          </b>
                        </div>
                        <div className="contact-value">
                          {item.docVerifyStatus}
                        </div>
                      </div>
                    </li>
                  </div>
                  <li className="tenant-info mt-3">
                    <div className="p-2">
                      <div className="contact-label">
                        <b>Aadhar Front</b>
                      </div>
                      <div className="contact-value">
                        <img
                          src={item.aadharFile}
                          alt="Aadhar Front"
                          className="kyc-img"
                        />
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="contact-label">
                        <b>Aadhar Back</b>
                      </div>
                      <div className="contact-value">
                        <img
                          src={item.aadharFileBack}
                          alt="Aadhar Back"
                          className="kyc-img"
                        />
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="contact-label">
                        <b>PAN</b>
                      </div>
                      <div className="contact-value">
                        <img
                          src={item.panFile}
                          alt="PAN File"
                          className="kyc-img"
                        />
                      </div>
                    </div>
                  </li>
                </ul>
              ))}
            </ul>
          ) : (
            <p>No data available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTenantModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Flatmate Modal ────────────────────────────────────────────────────── */}
      <Modal
        show={showFlatmateModal}
        onHide={() => setShowFlatmateModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {flatmateData.length ? (
            <DataTable
              value={flatmateData}
              paginator
              rows={5}
              showGridlines
              stripedRows
            >
              <Column body={flatmateName} header="Flatmate Name" />
              <Column field="relationship" header="Relationship" />
              <Column field="gender" header="Gender" />
              <Column field="age" header="Age" />
              <Column field="idType" header="Id Type" />
              <Column field="idNo" header="Id No" />
            </DataTable>
          ) : (
            <p>No data available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowFlatmateModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Flat Occupancy Report Modal ───────────────────────────────────────── */}
      <Modal
        show={showFlatOccupancyReport.show}
        onHide={handleCloseFlatOccupancyModal}
        size="xl"
          enforceFocus={false} 
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Flat Occupancy Report
            {/* {showFlatOccupancyReport.flatName
              ? ` — ${showFlatOccupancyReport.flatName}`
              : ""} */}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* ── Filter Bar ── */}
          <div className="filter-bar">

            {/* From Month */}
            <div>
              <label>Month</label>
              <Calendar
                value={flatFromMonth}
                onChange={(e) => setFlatFromMonth(e.value)}
                view="month"
                dateFormat="M-yy"
                placeholder="Select From Month"
                maxDate={flatToMonth || new Date()}
                showIcon
                inputClassName="form-control"
                style={{ width: "180px" }}
              />
            </div>

            {/* To Month */}
            {/* <div>
              <label>To Month</label>
              <Calendar
                value={flatToMonth}
                onChange={(e) => setFlatToMonth(e.value)}
                view="month"
                dateFormat="M-yy"
                placeholder="Select To Month"
                minDate={flatFromMonth || undefined}
                maxDate={new Date()}
                showIcon
                inputClassName="form-control"
                style={{ width: "180px" }}
              />
            </div> */}

            {/* ── Flat Dropdown ── */}
            <div>
              <label>Select Flat</label>
              <Dropdown
                value={selectedFlatOption}
                onChange={(e) => {
                  // e.value is the full { flatGuid, flatName } object
                  const chosen = e.value;
                  setShowFlatOccupancyReport((prev) => ({
                    ...prev,
                    flatGuid: chosen?.flatGuid ?? "",
                    flatName: chosen?.flatName ?? "",
                  }));
                }}
                options={flatnameWithGuid}
                optionLabel="flatName"
                // NOTE: do NOT set optionValue here — we want the full object as value
                // so selectedFlatOption lookup stays simple.
                placeholder="Select a flat"
                filter
                filterBy="flatName"
                filterPlaceholder="Search Flat"
                filterDelay={300}
                itemTemplate={FlatOptionTemplate}
                valueTemplate={FlatValueTemplate}
                appendTo={document.body}
                style={{ width: "200px" }}
                dropdownIcon={(opts) =>
                  opts.iconProps["data-pr-overlay-visible"] ? (
                    <ChevronRightIcon {...opts.iconProps} />
                  ) : (
                    <ChevronDownIcon {...opts.iconProps} />
                  )
                }
              />
            </div>

            <Button
              variant="primary"
              onClick={handleApplyFilter}
              className="py-2"
            >
              Apply Filter
            </Button>

            <Button variant="outline-secondary" onClick={handleResetFilter}>
              Reset
            </Button>

            
            <div style={{ marginLeft: "auto" }}>
              <Button
                variant="success"
                onClick={handleExportExcel}
                disabled={exportingExcel || flatOccupancyData.length === 0}
              >
                {exportingExcel ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    />
                    Exporting...
                  </>
                ) : (
                  "⬇ Export Excel"
                )}
              </Button>
            </div>
          </div>
          <div className="report-percentage d-flex gap-4 mb-1">
            <div className="report-card card p-3 rounded  mb-1 ">
              <span className="report-value fs-4 fw-bold mb-1 text-success">
               {flatOccupancyData[0]?.propertyOccupancyPercentage}%
                </span>
                <span className="small mt-auto">
                 Property Occupancy Percentage
                </span>
            </div>
            {
              showFlatOccupancyReport.flatGuid && (
                 <div className="report-card card p-3 rounded mb-1">
              <span className="report-value fs-4 fw-bold mb-1 text-primary">
  {flatOccupancyData[0]?.flatOccupancyPercentage}%
                </span>
                <span className="small ">
                  Flat Occupancy Percentage
                </span>
            </div>
              )
            }
              
          </div>

          {/* ── Occupancy DataTable ── */}
          {loadingFlatOccupancyReport ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <DataTable
              value={flatOccupancyData}
              paginator
              rows={reportsPerPage}
              totalRecords={reportTotalCount}
              lazy
              first={reportFirst}
              onPage={(e) => {
                setReportFirst(e.first);
                setReportCurrentPage(e.page);
                fetchFlatOccupancyReport(e.page);
              }}
              showGridlines
              stripedRows
              emptyMessage="No occupancy records found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
            >
              <Column
                header="S.No"
                body={(_, { rowIndex }) => reportFirst + rowIndex + 1}
                style={{ width: "60px" }}
              />
              <Column field="flatName" header="Flat Name" />
              <Column field="flatNo" header="Flat No" />
              <Column header="Period Type" body={periodTypeBadge} />
              <Column
                field="tenantName"
                header="Tenant Name"
                body={(rowData) => rowData.tenantName || "-"}
              />
              <Column
                field="tenantMobile"
                header="Tenant Mobile"
                body={(rowData) => rowData.tenantMobile || "-"}
              />
              <Column
                field="orderStatus"
                header="Order Status"
                body={(rowData) => rowData.orderStatus || "-"}
              />
              <Column
                header="Effective From"
                body={(rowData) => formatDate(rowData.effectiveFrom)}
              />
              <Column
                header="Effective To"
                body={(rowData) => formatDate(rowData.effectiveTo)}
              />
              <Column field="daysInRange" header="Days In Range" />
            </DataTable>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseFlatOccupancyModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OwnerProperty;